import { CHAT_GPT, TASK_SYSTEM_MESSAGES } from '../../common/Constants';
import { IStoryGeneratorApiClient } from './StoryGenerator.types';
import { ChatGptMessage, ChatGptResponse, ChatGptRole } from './ChatGpt.types';
import {
  AIProviderFactory,
  AIMessage,
  AIResponse,
  DEFAULT_MODEL_ID,
  getModelConfig
} from '../AIProviderService';
import { enqueueGenerationJob, waitForGenerationResult } from '../AsyncGenerationService';

const STORY_ITEM_SCHEMA = {
  type: 'object',
  properties: {
    s: { type: 'string' },
    d: { type: 'string' }
  },
  required: ['s', 'd'],
  additionalProperties: false
};

const STORY_ITEM_WITH_ID_SCHEMA = {
  type: 'object',
  properties: {
    id: { type: 'string' },
    s: { type: 'string' },
    d: { type: 'string' }
  },
  required: ['id', 's', 'd'],
  additionalProperties: false
};

const TASK_ITEM_SCHEMA = {
  type: 'object',
  properties: {
    s: { type: 'string' },
    d: { type: 'string' }
  },
  required: ['s', 'd'],
  additionalProperties: false
};

const TASK_ITEM_WITH_ID_SCHEMA = {
  type: 'object',
  properties: {
    id: { type: 'string' },
    s: { type: 'string' },
    d: { type: 'string' }
  },
  required: ['id', 's', 'd'],
  additionalProperties: false
};

const STORY_ITEM_MAP_SCHEMA = {
  type: 'object',
  properties: {},
  required: [],
  additionalProperties: STORY_ITEM_SCHEMA
};

const TASK_ITEM_MAP_SCHEMA = {
  type: 'object',
  properties: {},
  required: [],
  additionalProperties: TASK_ITEM_SCHEMA
};

const SUMMARY_DESCRIPTION_SCHEMA = {
  type: 'object',
  properties: {
    summary: { type: 'string' },
    description: { type: 'string' }
  },
  required: ['summary', 'description'],
  additionalProperties: false
};

const BULK_STORIES_SCHEMA = STORY_ITEM_MAP_SCHEMA;

const BULK_EPICS_SCHEMA = STORY_ITEM_MAP_SCHEMA;

const BULK_TASKS_SCHEMA = TASK_ITEM_MAP_SCHEMA;

const EPICS_AND_STORIES_SCHEMA = {
  type: 'object',
  properties: {
    epics: {
      type: 'object',
      properties: {},
      required: [],
      additionalProperties: {
        type: 'object',
        properties: {
          s: { type: 'string' },
          d: { type: 'string' },
          stories: STORY_ITEM_MAP_SCHEMA
        },
        required: ['s', 'd', 'stories'],
        additionalProperties: false
      }
    }
  },
  required: ['epics'],
  additionalProperties: false
};

const SINGLE_EPIC_MULTI_STORIES_SCHEMA = {
  type: 'object',
  properties: {
    epic: {
      type: 'object',
      properties: {
        s: { type: 'string' },
        d: { type: 'string' }
      },
      required: ['s', 'd'],
      additionalProperties: false
    },
    stories: {
      type: 'object',
      properties: {},
      required: [],
      additionalProperties: STORY_ITEM_SCHEMA
    }
  },
  required: ['epic', 'stories'],
  additionalProperties: false
};

const EPICS_AND_TASKS_SCHEMA = {
  type: 'object',
  properties: {
    epics: {
      type: 'object',
      properties: {},
      required: [],
      additionalProperties: {
        type: 'object',
        properties: {
          s: { type: 'string' },
          d: { type: 'string' },
          tasks: TASK_ITEM_MAP_SCHEMA
        },
        required: ['s', 'd', 'tasks'],
        additionalProperties: false
      }
    }
  },
  required: ['epics'],
  additionalProperties: false
};

const SINGLE_EPIC_MULTI_TASKS_SCHEMA = {
  type: 'object',
  properties: {
    epic: {
      type: 'object',
      properties: {
        s: { type: 'string' },
        d: { type: 'string' }
      },
      required: ['s', 'd'],
      additionalProperties: false
    },
    tasks: TASK_ITEM_MAP_SCHEMA
  },
  required: ['epic', 'tasks'],
  additionalProperties: false
};

const STORIES_AND_TASKS_SCHEMA = {
  type: 'object',
  properties: {
    stories: {
      type: 'object',
      properties: {},
      required: [],
      additionalProperties: {
        type: 'object',
        properties: {
          s: { type: 'string' },
          d: { type: 'string' },
          tasks: TASK_ITEM_MAP_SCHEMA
        },
        required: ['s', 'd', 'tasks'],
        additionalProperties: false
      }
    }
  },
  required: ['stories'],
  additionalProperties: false
};

const SINGLE_STORY_MULTI_TASKS_SCHEMA = {
  type: 'object',
  properties: {
    story: {
      type: 'object',
      properties: {
        s: { type: 'string' },
        d: { type: 'string' }
      },
      required: ['s', 'd'],
      additionalProperties: false
    },
    tasks: TASK_ITEM_MAP_SCHEMA
  },
  required: ['story', 'tasks'],
  additionalProperties: false
};

const ANTHROPIC_BULK_STORIES_SCHEMA = {
  type: 'array',
  items: STORY_ITEM_WITH_ID_SCHEMA
};

const ANTHROPIC_BULK_EPICS_SCHEMA = {
  type: 'array',
  items: STORY_ITEM_WITH_ID_SCHEMA
};

const ANTHROPIC_BULK_TASKS_SCHEMA = {
  type: 'array',
  items: TASK_ITEM_WITH_ID_SCHEMA
};

const ANTHROPIC_EPICS_AND_STORIES_SCHEMA = {
  type: 'object',
  properties: {
    epics: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          s: { type: 'string' },
          d: { type: 'string' },
          stories: {
            type: 'array',
            items: STORY_ITEM_WITH_ID_SCHEMA
          }
        },
        required: ['id', 's', 'd', 'stories'],
        additionalProperties: false
      }
    }
  },
  required: ['epics'],
  additionalProperties: false
};

const ANTHROPIC_SINGLE_EPIC_MULTI_STORIES_SCHEMA = {
  type: 'object',
  properties: {
    epic: {
      type: 'object',
      properties: {
        s: { type: 'string' },
        d: { type: 'string' }
      },
      required: ['s', 'd'],
      additionalProperties: false
    },
    stories: {
      type: 'array',
      items: STORY_ITEM_WITH_ID_SCHEMA
    }
  },
  required: ['epic', 'stories'],
  additionalProperties: false
};

const ANTHROPIC_EPICS_AND_TASKS_SCHEMA = {
  type: 'object',
  properties: {
    epics: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          s: { type: 'string' },
          d: { type: 'string' },
          tasks: {
            type: 'array',
            items: TASK_ITEM_WITH_ID_SCHEMA
          }
        },
        required: ['id', 's', 'd', 'tasks'],
        additionalProperties: false
      }
    }
  },
  required: ['epics'],
  additionalProperties: false
};

const ANTHROPIC_SINGLE_EPIC_MULTI_TASKS_SCHEMA = {
  type: 'object',
  properties: {
    epic: {
      type: 'object',
      properties: {
        s: { type: 'string' },
        d: { type: 'string' }
      },
      required: ['s', 'd'],
      additionalProperties: false
    },
    tasks: {
      type: 'array',
      items: TASK_ITEM_WITH_ID_SCHEMA
    }
  },
  required: ['epic', 'tasks'],
  additionalProperties: false
};

const ANTHROPIC_STORIES_AND_TASKS_SCHEMA = {
  type: 'object',
  properties: {
    stories: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          s: { type: 'string' },
          d: { type: 'string' },
          tasks: {
            type: 'array',
            items: TASK_ITEM_WITH_ID_SCHEMA
          }
        },
        required: ['id', 's', 'd', 'tasks'],
        additionalProperties: false
      }
    }
  },
  required: ['stories'],
  additionalProperties: false
};

const ANTHROPIC_SINGLE_STORY_MULTI_TASKS_SCHEMA = {
  type: 'object',
  properties: {
    story: {
      type: 'object',
      properties: {
        s: { type: 'string' },
        d: { type: 'string' }
      },
      required: ['s', 'd'],
      additionalProperties: false
    },
    tasks: {
      type: 'array',
      items: TASK_ITEM_WITH_ID_SCHEMA
    }
  },
  required: ['story', 'tasks'],
  additionalProperties: false
};

type ResponseSchemaKind =
  | 'bulkStories'
  | 'bulkEpics'
  | 'bulkTasks'
  | 'epicsAndStories'
  | 'epicsAndTasks'
  | 'singleEpic'
  | 'singleStory'
  | 'singleTask'
  | 'singleEpicMultiStories'
  | 'singleEpicMultiTasks'
  | 'storiesAndTasks'
  | 'singleStoryMultiTasks';

const getProviderSchema = (provider: string, kind: ResponseSchemaKind) => {
  if (provider === 'anthropic') {
    switch (kind) {
      case 'bulkStories':
        return ANTHROPIC_BULK_STORIES_SCHEMA;
      case 'bulkEpics':
        return ANTHROPIC_BULK_EPICS_SCHEMA;
      case 'bulkTasks':
        return ANTHROPIC_BULK_TASKS_SCHEMA;
      case 'epicsAndStories':
        return ANTHROPIC_EPICS_AND_STORIES_SCHEMA;
      case 'epicsAndTasks':
        return ANTHROPIC_EPICS_AND_TASKS_SCHEMA;
      case 'singleEpicMultiStories':
        return ANTHROPIC_SINGLE_EPIC_MULTI_STORIES_SCHEMA;
      case 'singleEpicMultiTasks':
        return ANTHROPIC_SINGLE_EPIC_MULTI_TASKS_SCHEMA;
      case 'storiesAndTasks':
        return ANTHROPIC_STORIES_AND_TASKS_SCHEMA;
      case 'singleStoryMultiTasks':
        return ANTHROPIC_SINGLE_STORY_MULTI_TASKS_SCHEMA;
      case 'singleEpic':
      case 'singleStory':
      case 'singleTask':
      default:
        return SUMMARY_DESCRIPTION_SCHEMA;
    }
  }

  switch (kind) {
    case 'bulkStories':
      return BULK_STORIES_SCHEMA;
    case 'bulkEpics':
      return BULK_EPICS_SCHEMA;
    case 'bulkTasks':
      return BULK_TASKS_SCHEMA;
    case 'epicsAndStories':
      return EPICS_AND_STORIES_SCHEMA;
    case 'epicsAndTasks':
      return EPICS_AND_TASKS_SCHEMA;
    case 'singleEpicMultiStories':
      return SINGLE_EPIC_MULTI_STORIES_SCHEMA;
    case 'singleEpicMultiTasks':
      return SINGLE_EPIC_MULTI_TASKS_SCHEMA;
    case 'storiesAndTasks':
      return STORIES_AND_TASKS_SCHEMA;
    case 'singleStoryMultiTasks':
      return SINGLE_STORY_MULTI_TASKS_SCHEMA;
    case 'singleEpic':
    case 'singleStory':
    case 'singleTask':
    default:
      return SUMMARY_DESCRIPTION_SCHEMA;
  }
};

type StoryItem = { id: string; s: string; d: string };
type StoryMap = Record<string, { s: string; d: string }>;
type EpicWithStories = { id: string; s: string; d: string; stories: StoryItem[] };
type EpicMap = Record<string, { s: string; d: string; stories: StoryMap }>;
type TaskItem = { id: string; s: string; d: string };
type EpicWithTasks = { id: string; s: string; d: string; tasks: TaskItem[] };
type StoryWithTasks = { id: string; s: string; d: string; tasks: TaskItem[] };

const mapArrayToIdMap = (items: StoryItem[]): StoryMap => {
  return items.reduce((acc: StoryMap, item: StoryItem) => {
    if (item?.id) {
      acc[item.id] = { s: item.s, d: item.d };
    }
    return acc;
  }, {});
};

const transformAnthropicArrayResponse = (kind: ResponseSchemaKind, content: string): string => {
  try {
    const parsed = JSON.parse(content);
    if (!parsed) {
      return content;
    }

    if (kind === 'bulkStories' && Array.isArray(parsed)) {
      return JSON.stringify(mapArrayToIdMap(parsed));
    }

    if (kind === 'bulkEpics' && Array.isArray(parsed)) {
      return JSON.stringify(mapArrayToIdMap(parsed));
    }

    if (kind === 'bulkTasks' && Array.isArray(parsed)) {
      return JSON.stringify(mapArrayToIdMap(parsed));
    }

    if (kind === 'epicsAndStories' && Array.isArray(parsed?.epics)) {
      const epicsMap = parsed.epics.reduce((acc: EpicMap, epic: EpicWithStories) => {
        if (epic?.id) {
          acc[epic.id] = {
            s: epic.s,
            d: epic.d,
            stories: mapArrayToIdMap(Array.isArray(epic.stories) ? epic.stories : [])
          };
        }
        return acc;
      }, {} as EpicMap);
      return JSON.stringify({ epics: epicsMap });
    }

    if (kind === 'epicsAndTasks' && Array.isArray(parsed?.epics)) {
      const epicsMap = parsed.epics.reduce((acc: Record<string, { s: string; d: string; tasks: StoryMap }>, epic: EpicWithTasks) => {
        if (epic?.id) {
          acc[epic.id] = {
            s: epic.s,
            d: epic.d,
            tasks: mapArrayToIdMap(Array.isArray(epic.tasks) ? epic.tasks : [])
          };
        }
        return acc;
      }, {} as Record<string, { s: string; d: string; tasks: StoryMap }>);
      return JSON.stringify({ epics: epicsMap });
    }

    if (kind === 'singleEpicMultiStories' && Array.isArray(parsed?.stories)) {
      return JSON.stringify({
        epic: parsed.epic,
        stories: mapArrayToIdMap(parsed.stories)
      });
    }

    if (kind === 'singleEpicMultiTasks' && Array.isArray(parsed?.tasks)) {
      return JSON.stringify({
        epic: parsed.epic,
        tasks: mapArrayToIdMap(parsed.tasks)
      });
    }

    if (kind === 'storiesAndTasks' && Array.isArray(parsed?.stories)) {
      const storiesMap = parsed.stories.reduce((acc: Record<string, { s: string; d: string; tasks: StoryMap }>, story: StoryWithTasks) => {
        if (story?.id) {
          acc[story.id] = {
            s: story.s,
            d: story.d,
            tasks: mapArrayToIdMap(Array.isArray(story.tasks) ? story.tasks : [])
          };
        }
        return acc;
      }, {} as Record<string, { s: string; d: string; tasks: StoryMap }>);
      return JSON.stringify({ stories: storiesMap });
    }

    if (kind === 'singleStoryMultiTasks' && Array.isArray(parsed?.tasks)) {
      return JSON.stringify({
        story: parsed.story,
        tasks: mapArrayToIdMap(parsed.tasks)
      });
    }

    return content;
  } catch (err) {
    return content;
  }
};

/**
 * ChatGptApiClient
 * 
 * Main service for generating Epics, Stories, and other Jira items using AI.
 * Now supports multi-model generation through AIProviderFactory.
 */
export class ChatGptApiClient implements IStoryGeneratorApiClient {
  
  /**
   * Generate user stories using AI
   * 
   * @param userNotes - User's input describing the stories to generate
   * @param isChatGpt4 - Legacy parameter (deprecated, use modelId instead)
   * @param messages - Conversation history
   * @param modelId - Optional model ID for multi-model support
   */
  async generateUserStories(
    userNotes: string,
    _isChatGpt4: boolean,
    messages: ChatGptMessage[],
    modelId?: string
  ): Promise<ChatGptResponse | undefined> {
    const resolvedModelId = modelId ?? DEFAULT_MODEL_ID;
    return this.generateWithProvider(
      userNotes,
      messages,
      resolvedModelId,
      CHAT_GPT.SYSTEM_MESSAGES.USER_STORIES,
      'bulkStories',
      true
    );
  }

  /**
   * Generate epics using AI
   */
  async generateEpics(
    userNotes: string,
    _isChatGpt4: boolean,
    messages: ChatGptMessage[],
    modelId?: string
  ): Promise<ChatGptResponse | undefined> {
    const resolvedModelId = modelId ?? DEFAULT_MODEL_ID;
    return this.generateWithProvider(
      userNotes,
      messages,
      resolvedModelId,
      CHAT_GPT.SYSTEM_MESSAGES.EPICS,
      'bulkEpics',
      true
    );
  }

  /**
   * Generate a single epic using AI
   */
  async generateSingleEpic(
    userNotes: string,
    _isChatGpt4: boolean,
    messages: ChatGptMessage[],
    modelId?: string
  ): Promise<ChatGptResponse | undefined> {
    const resolvedModelId = modelId ?? DEFAULT_MODEL_ID;
    return this.generateWithProvider(
      userNotes,
      messages,
      resolvedModelId,
      CHAT_GPT.SYSTEM_MESSAGES.SINGLE_EPIC,
      'singleEpic',
      false // Don't add the "no commentary" message for single items
    );
  }

  /**
   * Generate a single story using AI
   */
  async generateSingleStory(
    userNotes: string,
    _isChatGpt4: boolean,
    messages: ChatGptMessage[],
    modelId?: string
  ): Promise<ChatGptResponse | undefined> {
    const resolvedModelId = modelId ?? DEFAULT_MODEL_ID;
    return this.generateWithProvider(
      userNotes,
      messages,
      resolvedModelId,
      CHAT_GPT.SYSTEM_MESSAGES.SINGLE_STORY,
      'singleStory',
      false
    );
  }

  /**
   * Generate epics and user stories using AI
   */
  async generateEpicsAndUserStories(
    userNotes: string,
    _isChatGpt4: boolean,
    messages: ChatGptMessage[],
    modelId?: string
  ): Promise<ChatGptResponse | undefined> {
    const resolvedModelId = modelId ?? DEFAULT_MODEL_ID;
    return this.generateWithProvider(
      userNotes,
      messages,
      resolvedModelId,
      CHAT_GPT.SYSTEM_MESSAGES.EPICS_AND_USER_STORIES,
      'epicsAndStories',
      true
    );
  }

  /**
   * Generate a single epic with multiple user stories using AI
   */
  async generateSingleEpicAndUserStories(
    userNotes: string,
    _isChatGpt4: boolean,
    messages: ChatGptMessage[],
    modelId?: string
  ): Promise<ChatGptResponse | undefined> {
    const resolvedModelId = modelId ?? DEFAULT_MODEL_ID;
    return this.generateWithProvider(
      userNotes,
      messages,
      resolvedModelId,
      CHAT_GPT.SYSTEM_MESSAGES.SINGLE_EPIC_AND_USER_STORIES,
      'singleEpicMultiStories',
      true
    );
  }

  /**
   * Generate multiple tasks using AI
   */
  async generateTasks(
    userNotes: string,
    _isChatGpt4: boolean,
    messages: ChatGptMessage[],
    modelId?: string
  ): Promise<ChatGptResponse | undefined> {
    const resolvedModelId = modelId ?? DEFAULT_MODEL_ID;
    return this.generateWithProvider(
      userNotes,
      messages,
      resolvedModelId,
      TASK_SYSTEM_MESSAGES.MULTIPLE_TASKS,
      'bulkTasks',
      true
    );
  }

  /**
   * Generate a single task using AI
   */
  async generateSingleTask(
    userNotes: string,
    _isChatGpt4: boolean,
    messages: ChatGptMessage[],
    modelId?: string
  ): Promise<ChatGptResponse | undefined> {
    const resolvedModelId = modelId ?? DEFAULT_MODEL_ID;
    return this.generateWithProvider(
      userNotes,
      messages,
      resolvedModelId,
      TASK_SYSTEM_MESSAGES.SINGLE_TASK,
      'singleTask',
      false
    );
  }

  /**
   * Generate epics and tasks using AI
   */
  async generateEpicsAndTasks(
    userNotes: string,
    _isChatGpt4: boolean,
    messages: ChatGptMessage[],
    modelId?: string
  ): Promise<ChatGptResponse | undefined> {
    const resolvedModelId = modelId ?? DEFAULT_MODEL_ID;
    return this.generateWithProvider(
      userNotes,
      messages,
      resolvedModelId,
      TASK_SYSTEM_MESSAGES.EPICS_AND_TASKS,
      'epicsAndTasks',
      true
    );
  }

  /**
   * Generate a single epic with tasks using AI
   */
  async generateSingleEpicAndTasks(
    userNotes: string,
    _isChatGpt4: boolean,
    messages: ChatGptMessage[],
    modelId?: string
  ): Promise<ChatGptResponse | undefined> {
    const resolvedModelId = modelId ?? DEFAULT_MODEL_ID;
    return this.generateWithProvider(
      userNotes,
      messages,
      resolvedModelId,
      TASK_SYSTEM_MESSAGES.SINGLE_EPIC_AND_TASKS,
      'singleEpicMultiTasks',
      true
    );
  }

  /**
   * Generate stories and tasks using AI
   */
  async generateStoriesAndTasks(
    userNotes: string,
    _isChatGpt4: boolean,
    messages: ChatGptMessage[],
    modelId?: string
  ): Promise<ChatGptResponse | undefined> {
    const resolvedModelId = modelId ?? DEFAULT_MODEL_ID;
    return this.generateWithProvider(
      userNotes,
      messages,
      resolvedModelId,
      TASK_SYSTEM_MESSAGES.STORIES_AND_TASKS,
      'storiesAndTasks',
      true
    );
  }

  /**
   * Generate a single story with tasks using AI
   */
  async generateSingleStoryAndTasks(
    userNotes: string,
    _isChatGpt4: boolean,
    messages: ChatGptMessage[],
    modelId?: string
  ): Promise<ChatGptResponse | undefined> {
    const resolvedModelId = modelId ?? DEFAULT_MODEL_ID;
    return this.generateWithProvider(
      userNotes,
      messages,
      resolvedModelId,
      TASK_SYSTEM_MESSAGES.SINGLE_STORY_AND_TASKS,
      'singleStoryMultiTasks',
      true
    );
  }

  /**
   * Generate content using the AIProviderFactory (new multi-model approach)
   * 
   * @param userNotes - User's input
   * @param messages - Conversation history
   * @param modelId - Model ID to use
   * @param systemMessages - System messages for the generation context
   * @param addNoCommentary - Whether to add the "no commentary" instruction
   */
  private async generateWithProvider(
    userNotes: string,
    messages: ChatGptMessage[],
    modelId: string,
    systemMessages: ChatGptMessage[],
    schemaKind: ResponseSchemaKind,
    addNoCommentary = true
  ): Promise<ChatGptResponse | undefined> {
    try {
      const provider = AIProviderFactory.getProviderForModel(modelId);
      const responseSchema = getProviderSchema(provider.provider, schemaKind);
      
      // Build system prompt from system messages
      const systemPrompt = systemMessages
        .filter(m => m.role === ChatGptRole.System)
        .map(m => m.content)
        .join('\n\n');
      
      // Convert conversation history and user input to AIMessage format
      const aiMessages: AIMessage[] = [
        // Add user messages from system messages (like the "I will provide..." message)
        ...systemMessages
          .filter(m => m.role === ChatGptRole.User)
          .map(m => ({ role: 'user' as const, content: m.content })),
        // Add conversation history
        ...messages.map(m => ({
          role: m.role === ChatGptRole.System ? 'system' as const : 
                m.role === ChatGptRole.User ? 'user' as const : 'assistant' as const,
          content: m.content
        })),
        // Add the current user input
        { role: 'user' as const, content: userNotes }
      ];
      
      // Add "no commentary" instruction if needed
      if (addNoCommentary && CHAT_GPT.SYSTEM_MESSAGES.DONT_PROVIDE_ADDITIONAL_COMMENTRY) {
        aiMessages.push({
          role: 'user' as const,
          content: CHAT_GPT.SYSTEM_MESSAGES.DONT_PROVIDE_ADDITIONAL_COMMENTRY.content
        });
      }
      
      const modelConfig = getModelConfig(modelId);

      const { requestId } = await enqueueGenerationJob({
        provider: provider.provider,
        modelName: modelConfig?.name || modelId,
        messages: aiMessages,
        systemPrompt: systemPrompt,
        temperature: CHAT_GPT.TEMPERATURE,
        jsonMode: true,
        responseSchema,
        maxTokens: modelConfig?.maxOutputTokens ?? modelConfig?.maxTokens,
        requestType: 'story'
      });

      const response = await waitForGenerationResult(requestId);
      const normalizedResponse =
        provider.provider === 'anthropic'
          ? { ...response, content: transformAnthropicArrayResponse(schemaKind, response.content) }
          : response;
      
      // Convert to ChatGptResponse format for backward compatibility
      return this.convertToLegacyResponse(normalizedResponse);
    } catch (error) {
      console.error('Error generating with provider:', error);
      throw error;
    }
  }

  /**
   * Convert AIResponse to ChatGptResponse for backward compatibility
   */
  private convertToLegacyResponse(response: AIResponse): ChatGptResponse {
    return {
      id: `gen-${Date.now()}`,
      object: 'chat.completion',
      created: Math.floor(Date.now() / 1000),
      model: response.model,
      usage: {
        prompt_tokens: response.usage.promptTokens,
        completion_tokens: response.usage.completionTokens,
        total_tokens: response.usage.totalTokens
      },
      choices: [{
        message: {
          role: 'assistant',
          content: response.content
        },
        finish_reason: response.finishReason,
        index: 0
      }]
    };
  }
}
